/**
 * Properties Tab Icon Set
 * SVG icons for all accordion sections in the properties panel
 */

import React from 'react';

export interface IconProps {
  color?: string;
  size?: number;
  className?: string;
}

const iconStyle = (color = '#666', size = 16) => ({
  cursor: 'default',
  transition: 'color 200ms ease',
  flexShrink: 0,
});

// 📋 Basic Properties
export const BasicPropertiesIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-basic-properties ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Document/page */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    {/* Fold corner */}
    <polyline points="14 2 14 8 20 8" />
    {/* Lines */}
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="15" y2="16" />
  </svg>
);

// 🔄 State
export const StateIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-state ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Two switches/toggles */}
    <rect x="3" y="5" width="18" height="3" rx="1.5" />
    <circle cx="6" cy="6.5" r="1" fill={color} />

    <rect x="3" y="12" width="18" height="3" rx="1.5" />
    <circle cx="18" cy="13.5" r="1" fill={color} />
  </svg>
);

// 📐 Grid & Layout
export const GridLayoutIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-grid-layout ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Grid of 4 boxes */}
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

// ✅ Validation Rules
export const ValidationIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-validation ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Checkmark in circle */}
    <circle cx="12" cy="12" r="10" />
    <polyline points="8 12 11 15 16 9" />
  </svg>
);

// 📌 Default Value
export const DefaultValueIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-default-value ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Pin */}
    <path d="M12 2L15 8H21L17 11L19 17L12 13L5 17L7 11L3 8H9L12 2Z" fill={color} />
  </svg>
);

// 🎨 Styling
export const StylingIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-styling ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Palette */}
    <circle cx="12" cy="12" r="10" />
    <circle cx="8" cy="8" r="2" fill={color} />
    <circle cx="16" cy="8" r="2" fill={color} />
    <circle cx="16" cy="16" r="2" fill={color} />
    <circle cx="8" cy="16" r="2" fill={color} />
  </svg>
);

// ✨ Animations
export const AnimationsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-animations ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Magic wand */}
    <path d="M3 3L6 6M8 2L10 10M20 8L12 16M18 20L20 22" />
    <circle cx="12" cy="12" r="2" fill={color} />
  </svg>
);

// 📝 Scripts
export const ScriptsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-scripts ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Code brackets */}
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

// 📦 Container
export const ContainerIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-container ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Box */}
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

// ⚡ Actions
export const ActionsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-actions ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Lightning bolt */}
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

// 🌐 i18n
export const I18nIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-i18n ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Globe */}
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// 🎯 Icons
export const IconsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-icons ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Target/bullseye */}
    <circle cx="12" cy="12" r="1" fill={color} />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

// ♿ Accessibility
export const AccessibilityIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-accessibility ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Accessibility symbol - person with arms raised */}
    <circle cx="12" cy="4" r="2" />
    <path d="M12 6v4" />
    <path d="M8 10h8" />
    <path d="M10 14v6" />
    <path d="M14 14v6" />
    <path d="M8 20h8" />
  </svg>
);

// 🔒 Security
export const SecurityIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-security ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Lock */}
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="17" r="1" fill={color} />
  </svg>
);

// 📋 Container Basic
export const ContainerBasicIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-container-basic ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Layered boxes - representing container structure */}
    <rect x="3" y="6" width="18" height="3" />
    <rect x="3" y="11" width="18" height="3" />
    <rect x="3" y="16" width="18" height="3" />
  </svg>
);

// 📐 Container Layout
export const ContainerLayoutIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-container-layout ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Columns layout */}
    <line x1="3" y1="3" x2="3" y2="21" />
    <line x1="8" y1="3" x2="8" y2="21" />
    <line x1="13" y1="3" x2="13" y2="21" />
    <line x1="18" y1="3" x2="18" y2="21" />
    <line x1="3" y1="3" x2="21" y2="3" />
    <line x1="3" y1="21" x2="21" y2="21" />
  </svg>
);

// 👶 Child Forms
export const ChildFormsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-child-forms ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Parent form (large) with child forms (smaller) inside */}
    <rect x="2" y="4" width="20" height="16" rx="1" />
    <rect x="5" y="7" width="6" height="4" rx="1" />
    <rect x="13" y="7" width="6" height="4" rx="1" />
    <rect x="5" y="13" width="6" height="4" rx="1" />
    <rect x="13" y="13" width="6" height="4" rx="1" />
  </svg>
);

// 🔗 Binding
export const BindingIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-binding ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Link/chain for data binding */}
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// 💾 Save
export const SaveIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-save ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Floppy disk / save icon */}
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

// 🧭 Navigation
export const NavigationIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-navigation ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Navigation arrows */}
    <polyline points="12 5 19 12 12 19" />
    <polyline points="5 5 12 12 5 19" />
  </svg>
);

// 📑 Tabs (Left)
export const TabsLeftIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-tabs-left ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Left sidebar tabs */}
    <rect x="3" y="3" width="5" height="4" />
    <rect x="3" y="8" width="5" height="4" />
    <rect x="3" y="13" width="5" height="4" />
    <rect x="9" y="3" width="12" height="14" />
  </svg>
);

// 📊 Tabs (Top)
export const TabsTopIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-tabs-top ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Top horizontal tabs */}
    <rect x="3" y="3" width="4" height="3" />
    <rect x="8" y="3" width="4" height="3" />
    <rect x="13" y="3" width="4" height="3" />
    <rect x="3" y="7" width="14" height="10" />
  </svg>
);

// 🎚️ Accordion
export const AccordionIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-accordion ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Accordion sections */}
    <line x1="3" y1="4" x2="21" y2="4" />
    <rect x="3" y="4" width="18" height="3" />
    <line x1="3" y1="8" x2="21" y2="8" />
    <rect x="3" y="8" width="18" height="2" />
    <line x1="3" y1="11" x2="21" y2="11" />
    <rect x="3" y="11" width="18" height="2" />
    <line x1="3" y1="14" x2="21" y2="14" />
    <rect x="3" y="14" width="18" height="3" />
  </svg>
);

// 🧙 Wizard
export const WizardIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-wizard ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Wizard steps with arrows */}
    <circle cx="5" cy="12" r="2" fill={color} />
    <line x1="7" y1="12" x2="11" y2="12" />
    <circle cx="13" cy="12" r="2" fill={color} />
    <line x1="15" y1="12" x2="19" y2="12" />
    <circle cx="21" cy="12" r="2" fill={color} />
    {/* Progress indicator */}
    <line x1="5" y1="10" x2="5" y2="14" />
  </svg>
);

// 🎴 Card-Based
export const CardBasedIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-card-based ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Card with shadow effect */}
    <rect x="3" y="3" width="18" height="14" rx="1" />
    <line x1="6" y1="8" x2="18" y2="8" />
    <line x1="6" y1="12" x2="14" y2="12" />
    <line x1="20" y1="5" x2="20" y2="17" />
    <line x1="22" y1="7" x2="22" y2="15" />
  </svg>
);

// 🧭 Sidebar Nav
export const SidebarNavIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-sidebar-nav ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Sidebar with nav items */}
    <rect x="3" y="3" width="6" height="14" />
    <line x1="3" y1="6" x2="9" y2="6" />
    <line x1="3" y1="10" x2="9" y2="10" />
    <line x1="3" y1="14" x2="9" y2="14" />
    <rect x="10" y="3" width="11" height="14" />
  </svg>
);

// ⚙️ Control Configuration
export const ControlConfigIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-control-config ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Gear/settings for configuration */}
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m5.08 5.08l4.24 4.24M1 12h6m6 0h6M4.22 19.78l4.24-4.24m5.08-5.08l4.24-4.24" />
  </svg>
);

// 📋 Options/Select Configuration
export const OptionsIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-options ${className}`}
    style={iconStyle(color, size)}
  >
    {/* List/menu for options */}
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

// #️⃣ Number Configuration
export const NumberConfigIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-number-config ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Plus/minus for number stepping */}
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

// 📅 Date Configuration
export const DateConfigIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-date-config ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Calendar */}
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// 🎬 Media Configuration
export const MediaConfigIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-media-config ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Play button for media */}
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

// 📄 Document Configuration
export const DocumentConfigIcon: React.FC<IconProps> = ({ color = '#666', size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`icon-document-config ${className}`}
    style={iconStyle(color, size)}
  >
    {/* Document/file */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="15" y2="16" />
  </svg>
);

// Export all icons as a set
export const PropertiesIconSet = {
  BasicPropertiesIcon,
  StateIcon,
  GridLayoutIcon,
  ValidationIcon,
  DefaultValueIcon,
  StylingIcon,
  AnimationsIcon,
  ScriptsIcon,
  ContainerIcon,
  ActionsIcon,
  I18nIcon,
  IconsIcon,
  AccessibilityIcon,
  SecurityIcon,
  ContainerBasicIcon,
  ContainerLayoutIcon,
  ChildFormsIcon,
  BindingIcon,
  SaveIcon,
  NavigationIcon,
  TabsLeftIcon,
  TabsTopIcon,
  AccordionIcon,
  WizardIcon,
  CardBasedIcon,
  SidebarNavIcon,
  ControlConfigIcon,
  OptionsIcon,
  NumberConfigIcon,
  DateConfigIcon,
  MediaConfigIcon,
  DocumentConfigIcon,
};

export default PropertiesIconSet;
